/**
 * Auth Hooks Index
 *
 * This file exports all authentication-related hooks organized by functionality.
 * Downstream applications can import specific hooks or use the convenience hooks.
 */
// ============================================================================
// Core Hooks (fundamental auth functionality)
// ============================================================================
export * from './core-hooks';
// ============================================================================
// User Management Hooks
// ============================================================================
export * from './user-hooks';
// ============================================================================
// Workspace Management Hooks
// ============================================================================
export * from './workspace-hooks';
// ============================================================================
// Session Management Hooks
// ============================================================================
export * from './session-hooks';
export * from './auth-session';
// ============================================================================
// Permission and Role Hooks
// ============================================================================
export * from './permission-hooks';
// ============================================================================
// Auth Action Hooks (high-level operations)
// ============================================================================
export * from './magic-link-actions';
export * from './password-actions';
export * from './email-otp-hooks';
export * from './organization-actions';
export * from './session-actions';
export * from './oauth-actions';
export * from './invitation-actions';
export * from './mfa-hooks';
export * from './member-hooks';
// ============================================================================
// SSO Hooks
// ============================================================================
export * from './sso';
// ============================================================================
// Stytch Hooks (low-level Stytch hooks without AuthManager)
// ============================================================================
// Re-export Stytch hooks - these are low-level hooks that can be used directly
// or wrapped in abstracted hooks (preferred pattern for app code)
export { useStytchSession, useStytchSessionInfo, useStytchIsAuthenticated, useStytchMember, useStytchOrganization, useOrganization, useStytchSessionJWT, useStytchSessionToken, useStytchRefreshSession, useStytchRevokeSession, useStytchSessionManagement, } from '../stytch/session-hooks';
// ============================================================================
// Convenience Hooks (for easy consumption)
// ============================================================================
import { useAuth, useIsAuthenticated, useAuthLoading, useAuthError } from './core-hooks';
import { useUser, useCurrentUser } from './user-hooks';
import { useWorkspace, useCurrentWorkspace } from './workspace-hooks';
import { useSession } from './session-hooks';
import { usePermissions } from './permission-hooks';
/**
 * Convenience hook that provides all auth functionality in one place
 * Perfect for components that need comprehensive auth access
 */
export function useAuthComprehensive() {
    const auth = useAuth();
    const user = useUser();
    const workspace = useWorkspace();
    const session = useSession();
    const permissions = usePermissions();
    return {
        // Core auth state
        ...auth,
        // User data
        ...user,
        // Workspace data
        ...workspace,
        // Session data
        ...session,
        // Permission data
        ...permissions,
    };
}
/**
 * Convenience hook for components that only need basic auth info
 * Lightweight alternative to useAuthComprehensive
 */
export function useAuthBasic() {
    return {
        isAuthenticated: useIsAuthenticated(),
        isLoading: useAuthLoading(),
        error: useAuthError(),
        user: useCurrentUser(),
        workspace: useCurrentWorkspace(),
    };
}
